import { useCallback, useMemo, useRef, useState } from 'react';

import * as api from '@/features/canvas/api';
import { CommentThread, WhiteboardComment } from '@/features/canvas/types';

/**
 * Owns a whiteboard's comment threads with optimistic local edits, fire-and-forget persistence, and
 * realtime reconcile — the same discipline as useElements, minus the geometry batch and the undo
 * stack (comments aren't on the canvas Ctrl-Z stack, matching Miro; a delete is immediate + healed on
 * reconnect). A thread ROOT (parent_id null) is a pin at world (x,y); REPLIES point at it.
 */
export function useComments(whiteboardId: string, meId: string, meName: string) {
  const [comments, setComments] = useState<WhiteboardComment[]>([]);
  const ref = useRef<WhiteboardComment[]>(comments);
  ref.current = comments;
  // Per-member "seen" markers (thread root id → ISO time) for unread badges.
  const [seenMap, setSeenMap] = useState<Map<string, string>>(new Map());

  // Ref-synchronous mutators (keep ref.current current before the async setState, like useElements).
  const applyUpsert = useCallback((c: WhiteboardComment) => {
    const idx = ref.current.findIndex((x) => x.id === c.id);
    const next = idx === -1 ? [...ref.current, c] : ref.current.map((x) => (x.id === c.id ? c : x));
    ref.current = next;
    setComments(next);
  }, []);
  const applyPatch = useCallback((id: string, patch: Partial<WhiteboardComment>) => {
    const next = ref.current.map((x) => (x.id === id ? { ...x, ...patch } : x));
    ref.current = next;
    setComments(next);
  }, []);
  const applyDelete = useCallback((ids: string[]) => {
    const set = new Set(ids);
    const next = ref.current.filter((x) => !set.has(x.id));
    ref.current = next;
    setComments(next);
  }, []);

  // ---- realtime merges (other tabs/people) — own echoes are dropped upstream by client id ----
  const mergeUpsert = useCallback(
    (c: WhiteboardComment) => {
      const cur = ref.current.find((x) => x.id === c.id);
      if (cur && cur.updated_at && c.updated_at && c.updated_at < cur.updated_at) {
        // Stale for body/resolved/position — but reactions ride the same message and are NOT ordered by
        // updated_at (a reaction toggle doesn't bump it), so still take the incoming reaction snapshot,
        // else a peer's reaction is dropped just because we hold a newer body edit.
        if (JSON.stringify(cur.reactions ?? []) !== JSON.stringify(c.reactions ?? [])) {
          applyPatch(c.id, { reactions: c.reactions ?? [] });
        }
        return;
      }
      applyUpsert(c);
    },
    [applyUpsert, applyPatch],
  );
  const mergeDelete = applyDelete;

  const load = useCallback(async () => {
    const [list, seen] = await Promise.all([
      api.listComments(whiteboardId),
      api.listCommentsSeen(whiteboardId).catch(() => ({} as Record<string, string>)),
    ]);
    ref.current = list;
    setComments(list);
    // MERGE (don't replace): keep the newer seen marker per thread so a reconnect refetch can't revert
    // an in-flight optimistic markSeen (which stamps a local `now()` the server hasn't recorded yet).
    setSeenMap((prev) => {
      const merged = new Map(prev);
      for (const [k, v] of Object.entries(seen)) {
        const cur = merged.get(k);
        if (!cur || v > cur) merged.set(k, v);
      }
      return merged;
    });
  }, [whiteboardId]);

  const now = () => new Date().toISOString();

  /** Mark a thread seen for me (clears its unread badge) — optimistic + persisted. */
  const markSeen = useCallback((threadId: string) => {
    setSeenMap((m) => {
      const n = new Map(m);
      n.set(threadId, now());
      return n;
    });
    api.markThreadSeen(threadId).catch(() => {});
  }, []);

  /** Toggle my emoji reaction on a comment (optimistic; the comment's updated_at is deliberately NOT
   *  bumped so a peer's concurrent reaction echo — which also carries the unchanged updated_at — isn't
   *  dropped by the stale-echo guard). */
  const toggleReaction = useCallback(
    (commentId: string, emoji: string) => {
      const c = ref.current.find((x) => x.id === commentId);
      if (!c) return;
      const reactions = (c.reactions ?? []).map((r) => ({ ...r, members: [...r.members] }));
      const idx = reactions.findIndex((r) => r.emoji === emoji);
      if (idx === -1) {
        reactions.push({ emoji, members: [meId] });
      } else if (reactions[idx].members.includes(meId)) {
        reactions[idx].members = reactions[idx].members.filter((m) => m !== meId);
        if (reactions[idx].members.length === 0) reactions.splice(idx, 1);
      } else {
        reactions[idx].members.push(meId);
      }
      applyPatch(commentId, { reactions });
      // Reconcile to the server's authoritative reaction set when the toggle returns — this heals the
      // optimistic state if a peer's comment-upsert raced and clobbered our reaction mid-flight (the
      // server's own echo is skipped by client id, so this response is our only reconcile signal).
      api
        .reactComment(commentId, emoji)
        .then((fresh) => {
          if (fresh) applyPatch(commentId, { reactions: fresh.reactions ?? [] });
        })
        .catch(() => {});
    },
    [applyPatch, meId],
  );

  /** Drop a thread pin (root) at a world point. Optimistic; returns the created comment. */
  const createThread = useCallback(
    (x: number, y: number, body: string): WhiteboardComment => {
      const c: WhiteboardComment = {
        id: crypto.randomUUID(),
        whiteboard_id: whiteboardId,
        parent_id: null,
        author_id: meId || null,
        author_name: meName,
        x,
        y,
        body,
        resolved: false,
        created_at: now(),
        updated_at: now(),
      };
      applyUpsert(c);
      api.createComment(whiteboardId, { id: c.id, x, y, body }).catch(() => {});
      return c;
    },
    [whiteboardId, meId, meName, applyUpsert],
  );

  /** Add a reply to a thread. Optimistic; returns the created reply. */
  const reply = useCallback(
    (parentId: string, body: string): WhiteboardComment => {
      const c: WhiteboardComment = {
        id: crypto.randomUUID(),
        whiteboard_id: whiteboardId,
        parent_id: parentId,
        author_id: meId || null,
        author_name: meName,
        x: 0,
        y: 0,
        body,
        resolved: false,
        created_at: now(),
        updated_at: now(),
      };
      applyUpsert(c);
      api.createComment(whiteboardId, { id: c.id, parent_id: parentId, x: 0, y: 0, body }).catch(() => {});
      return c;
    },
    [whiteboardId, meId, meName, applyUpsert],
  );

  const edit = useCallback(
    (id: string, body: string) => {
      applyPatch(id, { body, updated_at: now() });
      api.updateComment(id, body).catch(() => {});
    },
    [applyPatch],
  );

  const toggleResolve = useCallback(
    (id: string) => {
      const c = ref.current.find((x) => x.id === id);
      if (!c) return;
      const resolved = !c.resolved;
      applyPatch(id, { resolved, updated_at: now() });
      api.resolveComment(id, resolved).catch(() => {});
    },
    [applyPatch],
  );

  /** Live pin drag: update local x/y every frame (no persist). */
  const livePin = useCallback(
    (id: string, x: number, y: number) => {
      applyPatch(id, { x, y });
    },
    [applyPatch],
  );

  /** Commit a finished pin drag: persist the new anchor. */
  const commitPin = useCallback((id: string, x: number, y: number) => {
    applyPatch(id, { x, y, updated_at: now() });
    api.moveComment(id, x, y).catch(() => {});
  }, [applyPatch]);

  /** Delete a thread (root removes its replies too) or a single reply. */
  const remove = useCallback(
    (id: string) => {
      const target = ref.current.find((x) => x.id === id);
      if (!target) return;
      const ids = target.parent_id ? [id] : [id, ...ref.current.filter((x) => x.parent_id === id).map((x) => x.id)];
      applyDelete(ids);
      api.deleteComment(id).catch(() => {});
    },
    [applyDelete],
  );

  // Group the flat list into threads (roots + their replies, replies oldest-first).
  const threads = useMemo<CommentThread[]>(() => {
    const roots = comments.filter((c) => !c.parent_id);
    const byParent = new Map<string, WhiteboardComment[]>();
    for (const c of comments) {
      if (!c.parent_id) continue;
      const arr = byParent.get(c.parent_id) ?? [];
      arr.push(c);
      byParent.set(c.parent_id, arr);
    }
    return roots.map((root) => {
      const replies = (byParent.get(root.id) ?? []).sort((a, b) => a.created_at.localeCompare(b.created_at));
      return { root, replies, count: 1 + replies.length };
    });
  }, [comments]);

  // Threads that are NEW TO ME: the latest comment isn't mine and is newer than my seen marker (or I've
  // never opened the thread). Opening a thread (markSeen) or posting to it clears it.
  const unread = useMemo(() => {
    const s = new Set<string>();
    for (const t of threads) {
      const latest = t.replies.length ? t.replies[t.replies.length - 1] : t.root;
      if (latest.author_id && latest.author_id === meId) continue; // my own latest → not unread
      const seenAt = seenMap.get(t.root.id);
      if (!seenAt || latest.created_at > seenAt) s.add(t.root.id);
    }
    return s;
  }, [threads, seenMap, meId]);

  return {
    comments,
    threads,
    unread,
    load,
    mergeUpsert,
    mergeDelete,
    createThread,
    reply,
    edit,
    toggleResolve,
    toggleReaction,
    markSeen,
    livePin,
    commitPin,
    remove,
  };
}
